import { useSyncExternalStore } from 'react';
import {
  quoteSheetName,
  safeSheetTitle,
  type SheetCell,
  type SheetTable,
} from '@/features/manufacturing/bomExport';

/**
 * Save a BOM straight into the user's Google Drive as a native Google Sheet.
 *
 * The browser does the whole thing — Atlas never sees the access token or
 * the file:
 *   1. Google Identity Services pops a sign-in / consent window for the
 *      non-sensitive `drive.file` scope (files this app creates, plus
 *      folders the user picks).
 *   2. The Google Picker pops Drive so the user can choose a folder.
 *   3. Drive API creates a spreadsheet in that folder; Sheets API writes
 *      the BOM and parts-list tabs.
 *
 * Cloud Console setup (same project as VITE_GOOGLE_CLIENT_ID):
 *   - Enable Google Drive API, Google Sheets API, and Google Picker API.
 *   - OAuth consent screen: add scope
 *     https://www.googleapis.com/auth/drive.file
 *   - OAuth client → Authorized JavaScript origins: the Atlas origin
 *     (http://localhost:5173, the Vercel host, the production domain).
 *     This flow uses a popup, so it does not need a redirect URI.
 *   - An API key (VITE_GOOGLE_API_KEY) restricted to the Picker API and
 *     to those HTTP referrers. The Picker requires it.
 *   - VITE_GOOGLE_APP_ID is the Cloud project *number*. It is inferred
 *     from the client id (`{number}-{hash}.apps.googleusercontent.com`)
 *     when the env var is omitted. The Picker needs it so the folder the
 *     user selects is granted to this app under drive.file.
 */

const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const EMAIL_SCOPE = 'https://www.googleapis.com/auth/userinfo.email';
const SCOPES = `${DRIVE_FILE_SCOPE} ${EMAIL_SCOPE}`;

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const GAPI_SRC = 'https://apis.google.com/js/api.js';

export interface DriveFolder {
  id: string;
  name: string;
}

export interface DriveSession {
  accessToken: string;
  expiresAt: number;
  email: string | null;
}

export interface CreatedGoogleSheet {
  id: string;
  url: string;
  name: string;
}

interface TokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface TokenClient {
  requestAccessToken: (override?: { prompt?: string }) => void;
}

interface PickerDoc {
  id: string;
  name: string;
}

interface PickerResponse {
  action: string;
  docs?: PickerDoc[];
}

interface PickerBuilder {
  setTitle: (title: string) => PickerBuilder;
  setOAuthToken: (token: string) => PickerBuilder;
  setDeveloperKey: (key: string) => PickerBuilder;
  setAppId: (appId: string) => PickerBuilder;
  setOrigin: (origin: string) => PickerBuilder;
  setCallback: (cb: (data: PickerResponse) => void) => PickerBuilder;
  addView: (view: DocsView) => PickerBuilder;
  enableFeature: (feature: string) => PickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
}

interface DocsView {
  setSelectFolderEnabled: (enabled: boolean) => DocsView;
  setIncludeFolders: (enabled: boolean) => DocsView;
  setParent: (parent: string) => DocsView;
  setEnableDrives: (enabled: boolean) => DocsView;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: TokenResponse) => void;
            error_callback?: (error: { type?: string; message?: string }) => void;
          }) => TokenClient;
        };
      };
      picker?: {
        Action: { PICKED: string; CANCEL: string };
        ViewId: { FOLDERS: string };
        Feature: { SUPPORT_DRIVES: string };
        DocsView: new (viewId: string) => DocsView;
        PickerBuilder: new () => PickerBuilder;
      };
    };
    gapi?: {
      load: (api: string, callback: () => void) => void;
    };
  }
}

let session: DriveSession | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function getDriveSession(): DriveSession | null {
  if (session && session.expiresAt <= Date.now()) {
    session = null;
  }
  return session;
}

function setSession(next: DriveSession | null) {
  session = next;
  emit();
}

export function clearDriveSession() {
  setSession(null);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The Google account this tab has signed in for Drive, if the token is still valid. */
export function useGoogleDriveSession(): DriveSession | null {
  return useSyncExternalStore(subscribe, getDriveSession, () => null);
}

/**
 * Cloud project number. The Picker grants `drive.file` access to the
 * chosen folder only when this matches the OAuth client’s project.
 */
export function googleCloudProjectNumber(
  clientId: string | undefined,
  override?: string,
): string | null {
  const fromEnv = override?.trim();
  if (fromEnv) return fromEnv;
  const match = /^(\d+)-/.exec(clientId?.trim() ?? '');
  return match?.[1] ?? null;
}

/** What’s missing before the Drive popup can run. Null when configured. */
export function googleSheetsConfigError(): string | null {
  if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
    return 'Google sign-in isn’t configured for this Atlas environment. Add VITE_GOOGLE_CLIENT_ID and redeploy.';
  }
  if (!import.meta.env.VITE_GOOGLE_API_KEY) {
    return 'Choosing a Google Drive folder isn’t configured for this Atlas environment. Add VITE_GOOGLE_API_KEY (a browser key with the Picker API enabled) and redeploy.';
  }
  if (
    !googleCloudProjectNumber(
      import.meta.env.VITE_GOOGLE_CLIENT_ID,
      import.meta.env.VITE_GOOGLE_APP_ID,
    )
  ) {
    return 'Google Drive can’t tell which Cloud project this app belongs to. Set VITE_GOOGLE_APP_ID to the project number.';
  }
  return null;
}

function loadScript(src: string): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[data-google-src="${src}"]`);
  if (existing?.dataset.loaded === 'true') return Promise.resolve();
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Could not reach Google. Check the connection and try again.')),
        { once: true },
      );
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.googleSrc = src;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () =>
      reject(new Error('Could not reach Google. Check the connection and try again.'));
    document.head.appendChild(script);
  });
}

async function loadGis() {
  if (window.google?.accounts?.oauth2) return;
  await loadScript(GIS_SRC);
  if (!window.google?.accounts?.oauth2) {
    throw new Error('Google sign-in failed to load.');
  }
}

async function loadPickerApi() {
  if (!window.gapi) await loadScript(GAPI_SRC);
  if (!window.gapi) throw new Error('Google Drive failed to load.');
  if (window.google?.picker) return;
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error('Google Drive took too long to load. Try again.')),
      15000,
    );
    window.gapi!.load('picker', () => {
      window.clearTimeout(timer);
      resolve();
    });
  });
  if (!window.google?.picker) throw new Error('Google Drive failed to load.');
}

function humanizeAuthError(code?: string, description?: string): string {
  if (code === 'popup_closed' || code === 'access_denied') {
    return 'Google sign-in was closed before Atlas was allowed to create a Sheet in Drive.';
  }
  if (code === 'popup_failed_to_open') {
    return 'The Google window was blocked. Allow popups for Atlas and try again.';
  }
  if (description) return description;
  if (code) return `Google sign-in failed (${code}).`;
  return 'Google sign-in failed.';
}

let tokenClient: TokenClient | null = null;
let pendingToken: {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
} | null = null;

function settleToken(error: Error | null, token?: string) {
  const pending = pendingToken;
  pendingToken = null;
  if (!pending) return;
  if (error || !token) pending.reject(error ?? new Error('Google sign-in failed.'));
  else pending.resolve(token);
}

/**
 * Popup sign-in. Returns a usable access token, from memory when the
 * previous grant is still valid — unless `selectAccount` forces the
 * account chooser.
 */
export async function signInToGoogleDrive(options?: {
  selectAccount?: boolean;
}): Promise<DriveSession> {
  const configError = googleSheetsConfigError();
  if (configError?.includes('VITE_GOOGLE_CLIENT_ID')) throw new Error(configError);

  const cached = getDriveSession();
  if (cached && !options?.selectAccount) return cached;

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) throw new Error('Google sign-in isn’t configured.');

  await loadGis();
  const oauth = window.google?.accounts?.oauth2;
  if (!oauth) throw new Error('Google sign-in failed to load.');

  if (!tokenClient) {
    tokenClient = oauth.initTokenClient({
      client_id: clientId,
      scope: SCOPES,
      callback: (response) => {
        if (response.error || !response.access_token) {
          settleToken(new Error(humanizeAuthError(response.error, response.error_description)));
          return;
        }
        const expiresIn = response.expires_in ?? 3600;
        setSession({
          accessToken: response.access_token,
          expiresAt: Date.now() + expiresIn * 1000 - 60_000,
          email: null,
        });
        settleToken(null, response.access_token);
      },
      error_callback: (error) => {
        settleToken(new Error(humanizeAuthError(error.type, error.message)));
      },
    });
  }

  if (pendingToken) {
    throw new Error('A Google sign-in window is already open.');
  }

  const accessToken = await new Promise<string>((resolve, reject) => {
    pendingToken = { resolve, reject };
    try {
      tokenClient!.requestAccessToken({
        prompt: options?.selectAccount ? 'select_account' : '',
      });
    } catch (err) {
      settleToken(err instanceof Error ? err : new Error('Google sign-in failed.'));
    }
  });

  const email = await fetchGoogleEmail(accessToken);
  const current = getDriveSession();
  const next: DriveSession = {
    accessToken,
    expiresAt: current?.expiresAt ?? Date.now() + 3500_000,
    email,
  };
  setSession(next);
  return next;
}

async function fetchGoogleEmail(accessToken: string): Promise<string | null> {
  try {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) return null;
    const profile = (await response.json()) as { email?: string };
    return profile.email ?? null;
  } catch {
    return null;
  }
}

/**
 * Google's Drive folder popup. Resolves null when the user dismisses it.
 * The Atlas dialog should be closed before this runs — the Picker is an
 * overlay on this page, and a modal dialog would sit on top of it.
 */
export async function openDriveFolderPicker(): Promise<DriveFolder | null> {
  const configError = googleSheetsConfigError();
  if (configError) throw new Error(configError);

  const current = getDriveSession();
  if (!current) throw new Error('Sign in with Google before choosing a folder.');

  await loadPickerApi();
  const pickerApi = window.google?.picker;
  if (!pickerApi) throw new Error('Google Drive failed to load.');

  const appId = googleCloudProjectNumber(
    import.meta.env.VITE_GOOGLE_CLIENT_ID,
    import.meta.env.VITE_GOOGLE_APP_ID,
  );
  if (!appId) throw new Error('Google Drive isn’t configured (missing project number).');

  const folders = new pickerApi.DocsView(pickerApi.ViewId.FOLDERS)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(true)
    .setParent('root')
    .setEnableDrives(true);

  return new Promise((resolve) => {
    const picker = new pickerApi.PickerBuilder()
      .setTitle('Choose a folder for this Google Sheet')
      .setOAuthToken(current.accessToken)
      .setDeveloperKey(import.meta.env.VITE_GOOGLE_API_KEY ?? '')
      .setAppId(appId)
      .setOrigin(window.location.origin)
      .enableFeature(pickerApi.Feature.SUPPORT_DRIVES)
      .addView(folders)
      .setCallback((data) => {
        if (data.action === pickerApi.Action.CANCEL) {
          resolve(null);
          return;
        }
        if (data.action !== pickerApi.Action.PICKED) return;
        const doc = data.docs?.[0];
        if (!doc?.id) {
          resolve(null);
          return;
        }
        resolve({ id: doc.id, name: doc.name?.trim() || 'Selected folder' });
      })
      .build();
    picker.setVisible(true);
  });
}

interface GoogleErrorBody {
  error?: { message?: string; status?: string };
}

async function googleJson<T>(url: string, accessToken: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const body = (await response.json().catch(() => ({}))) as T & GoogleErrorBody;
  if (!response.ok) {
    if (response.status === 401) clearDriveSession();
    throw new Error(googleErrorMessage(body, response.status));
  }
  return body;
}

function googleErrorMessage(body: GoogleErrorBody, status: number): string {
  const message = body.error?.message;
  if (status === 401) {
    return 'Google sign-in expired. Sign in again and choose the folder.';
  }
  if (message && /has not been used in project|is disabled|accessNotConfigured/i.test(message)) {
    return 'The Google Drive API or Google Sheets API is turned off for this Cloud project. Enable both, then try again.';
  }
  if (status === 403) {
    return (
      message ??
      'Google didn’t allow Atlas to create a file in that folder. Pick the folder again and approve access.'
    );
  }
  return message ?? `Google Drive returned an error (${status}).`;
}

const CURRENCY_HEADERS = new Set(['Unit cost', 'Ext. cost']);
const NUMBER_HEADERS = new Set(['Level', 'Qty / unit', 'Scrap %', 'Ext. qty', 'Total qty']);

/**
 * Create a native Google Sheet in `folder` and fill one tab per table.
 * Header row is frozen and bold; cost and quantity columns are numeric.
 */
export async function createSpreadsheetInFolder(args: {
  accessToken: string;
  title: string;
  folderId: string;
  tables: SheetTable[];
}): Promise<CreatedGoogleSheet> {
  const title = args.title.trim().slice(0, 200);
  if (!title) throw new Error('Give the Google Sheet a title.');
  if (args.tables.length === 0 || args.tables.every((table) => table.rows.length <= 1)) {
    throw new Error('This BOM has nothing to export.');
  }

  const file = await googleJson<{ id: string; webViewLink?: string }>(
    'https://www.googleapis.com/drive/v3/files?supportsAllDrives=true&fields=id,webViewLink',
    args.accessToken,
    {
      method: 'POST',
      body: JSON.stringify({
        name: title,
        mimeType: 'application/vnd.google-apps.spreadsheet',
        parents: [args.folderId],
      }),
    },
  );

  const meta = await googleJson<{
    sheets?: Array<{ properties?: { sheetId?: number } }>;
  }>(
    `https://sheets.googleapis.com/v4/spreadsheets/${file.id}?fields=sheets.properties.sheetId`,
    args.accessToken,
  );
  const firstSheetId = meta.sheets?.[0]?.properties?.sheetId;
  if (firstSheetId == null) {
    throw new Error('Google created the spreadsheet but didn’t return a tab to write into.');
  }

  const tabTitles = args.tables.map((table, index) =>
    safeSheetTitle(table.title, `Sheet ${index + 1}`),
  );

  const structure: unknown[] = [
    {
      updateSheetProperties: {
        properties: {
          sheetId: firstSheetId,
          title: tabTitles[0],
          gridProperties: { frozenRowCount: 1 },
        },
        fields: 'title,gridProperties.frozenRowCount',
      },
    },
  ];
  for (const tabTitle of tabTitles.slice(1)) {
    structure.push({
      addSheet: {
        properties: {
          title: tabTitle,
          gridProperties: { frozenRowCount: 1 },
        },
      },
    });
  }

  const updated = await googleJson<{
    replies?: Array<{ addSheet?: { properties?: { sheetId?: number } } }>;
  }>(`https://sheets.googleapis.com/v4/spreadsheets/${file.id}:batchUpdate`, args.accessToken, {
    method: 'POST',
    body: JSON.stringify({ requests: structure }),
  });

  const sheetIds = [firstSheetId];
  for (const reply of updated.replies ?? []) {
    const id = reply.addSheet?.properties?.sheetId;
    if (id != null) sheetIds.push(id);
  }

  await googleJson(
    `https://sheets.googleapis.com/v4/spreadsheets/${file.id}/values:batchUpdate`,
    args.accessToken,
    {
      method: 'POST',
      body: JSON.stringify({
        valueInputOption: 'USER_ENTERED',
        data: args.tables.map((table, index) => ({
          range: `${quoteSheetName(tabTitles[index] ?? 'Sheet')}!A1`,
          values: table.rows.map((row) => row.map((cell) => (cell == null ? '' : cell))),
        })),
      }),
    },
  );

  const formatting = args.tables.flatMap((table, index) =>
    formatRequests(sheetIds[index], table.rows),
  );
  if (formatting.length > 0) {
    await googleJson(
      `https://sheets.googleapis.com/v4/spreadsheets/${file.id}:batchUpdate`,
      args.accessToken,
      {
        method: 'POST',
        body: JSON.stringify({ requests: formatting }),
      },
    );
  }

  return {
    id: file.id,
    name: title,
    url: file.webViewLink ?? `https://docs.google.com/spreadsheets/d/${file.id}/edit`,
  };
}

function formatRequests(sheetId: number | undefined, rows: SheetCell[][]): unknown[] {
  if (sheetId == null || rows.length === 0) return [];
  const headers = rows[0] ?? [];
  const columnCount = Math.max(headers.length, 1);
  const rowCount = rows.length;
  const requests: unknown[] = [
    {
      repeatCell: {
        range: {
          sheetId,
          startRowIndex: 0,
          endRowIndex: 1,
          startColumnIndex: 0,
          endColumnIndex: columnCount,
        },
        cell: {
          userEnteredFormat: {
            textFormat: { bold: true },
            backgroundColor: { red: 0.93, green: 0.95, blue: 0.92 },
          },
        },
        fields: 'userEnteredFormat(textFormat,backgroundColor)',
      },
    },
    {
      autoResizeDimensions: {
        dimensions: {
          sheetId,
          dimension: 'COLUMNS',
          startIndex: 0,
          endIndex: columnCount,
        },
      },
    },
  ];

  if (rowCount <= 1) return requests;

  headers.forEach((header, column) => {
    if (typeof header !== 'string') return;
    const numberFormat = CURRENCY_HEADERS.has(header)
      ? { type: 'CURRENCY', pattern: '$#,##0.00' }
      : NUMBER_HEADERS.has(header)
        ? { type: 'NUMBER', pattern: '#,##0.####' }
        : null;
    if (!numberFormat) return;
    requests.push({
      repeatCell: {
        range: {
          sheetId,
          startRowIndex: 1,
          endRowIndex: rowCount,
          startColumnIndex: column,
          endColumnIndex: column + 1,
        },
        cell: { userEnteredFormat: { numberFormat } },
        fields: 'userEnteredFormat.numberFormat',
      },
    });
  });

  return requests;
}
